import { HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '@root/src/environments/environment';
import { Observable, Subject, catchError, map, of, switchMap } from 'rxjs';
import { ExternalScenesApiService } from './external-scenes-api.service';
import {
  ExternalSceneDto,
  ExternalSceneLoadState,
  ExternalSceneProviderError,
  ExternalSceneProvider,
  ExternalScenesFilterState,
  ExternalScenesSearchRequest,
  ExternalScenesSearchResponse
} from './external-scenes.models';
import { loadErrorKey, searchErrorKey } from './external-scenes-error.util';
import { buildSearchRequest } from './external-scenes-request.util';
import { computeCounters, sceneKey, toRowViewModel } from './external-scenes-row.util';

type SearchOutcome = { response: ExternalScenesSearchResponse } | { errorKey: string };

/** Event emitted when a Load request was accepted (the page shows the "request created" toast). */
export interface ExternalSceneLoadCreated {
  rowId: string;
  assignmentId: string;
}

/**
 * Signal store of the `download` tab. Provide it in the page component's `providers` so its lifetime (and
 * the in-flight request cancellation via `takeUntilDestroyed`) follows the page.
 */
@Injectable()
export class ExternalScenesStateService {
  private readonly api = inject(ExternalScenesApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly searchRequests$ = new Subject<ExternalScenesSearchRequest>();
  private readonly loadCreatedSubject = new Subject<ExternalSceneLoadCreated>();

  private readonly scenesState = signal<readonly ExternalSceneDto[]>([]);
  private readonly loadStatesState = signal<ReadonlyMap<string, ExternalSceneLoadState>>(new Map());

  /** True while a Search is in flight. */
  readonly isSearching = signal(false);
  /** True once the first Search of the session completed (distinguishes "not searched" from "empty"). */
  readonly hasSearched = signal(false);
  /** Full i18n key of the last validation or Search error; `null` when none. */
  readonly errorKey = signal<string | null>(null);
  readonly providerErrors = signal<readonly ExternalSceneProviderError[]>([]);
  readonly truncatedProviders = signal<readonly ExternalSceneProvider[]>([]);

  /** Emits after each accepted Load; for the toast. */
  readonly loadCreated$: Observable<ExternalSceneLoadCreated> = this.loadCreatedSubject.asObservable();

  readonly rows = computed(() => {
    const states = this.loadStatesState();
    return this.scenesState().map(scene =>
      toRowViewModel(scene, states.get(sceneKey(scene.provider, scene.externalId)), environment.api)
    );
  });
  readonly counters = computed(() => computeCounters(this.scenesState(), this.loadStatesState()));
  readonly isEmpty = computed(() => this.hasSearched() && !this.isSearching() && this.scenesState().length === 0);

  constructor() {
    this.searchRequests$
      .pipe(
        // A new search cancels the previous in-flight one.
        switchMap(request => this.runSearch(request)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(outcome => this.applySearchOutcome(outcome));
  }

  /** Validates the filters and, when valid, starts a Search (cancelling any running one). */
  search(filters: ExternalScenesFilterState): void {
    const built = buildSearchRequest(filters);
    if (!built.ok) {
      this.clearResults();
      this.errorKey.set(`EXTERNAL_SCENES.${built.errorKey}`);
      return;
    }
    this.errorKey.set(null);
    this.isSearching.set(true);
    this.searchRequests$.next(built.request);
  }

  /** Starts a single-scene Load for a row; ignored unless the row can be loaded. No catalog reload follows. */
  load(rowId: string): void {
    const row = this.rows().find(candidate => candidate.id === rowId);
    if (!row || !row.canLoad) {
      return;
    }
    this.setLoadState(rowId, { status: 'loading', errorKey: null, assignmentId: null });
    this.api
      .load({ provider: row.provider, externalId: row.externalId })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.setLoadState(rowId, { status: 'pending', errorKey: null, assignmentId: response.assignmentId });
          this.loadCreatedSubject.next({ rowId, assignmentId: response.assignmentId });
        },
        error: (error: unknown) =>
          this.setLoadState(rowId, { status: 'error', errorKey: loadErrorKey(statusOf(error)), assignmentId: null })
      });
  }

  /** Clears results and per-row state (e.g. when leaving the tab). */
  reset(): void {
    this.scenesState.set([]);
    this.loadStatesState.set(new Map());
    this.providerErrors.set([]);
    this.truncatedProviders.set([]);
    this.errorKey.set(null);
    this.hasSearched.set(false);
    this.isSearching.set(false);
  }

  private runSearch(request: ExternalScenesSearchRequest): Observable<SearchOutcome> {
    return this.api.search(request).pipe(
      map((response): SearchOutcome => ({ response })),
      catchError((error: unknown) => of<SearchOutcome>({ errorKey: searchErrorKey(statusOf(error)) }))
    );
  }

  /** A failed or rejected search must not leave the previous result on screen: it no longer matches the filters. */
  private clearResults(): void {
    this.scenesState.set([]);
    this.providerErrors.set([]);
    this.truncatedProviders.set([]);
    this.loadStatesState.set(new Map());
  }

  private applySearchOutcome(outcome: SearchOutcome): void {
    this.isSearching.set(false);
    this.hasSearched.set(true);
    if ('errorKey' in outcome) {
      this.clearResults();
      this.errorKey.set(outcome.errorKey);
      return;
    }
    const { response } = outcome;
    this.scenesState.set(response.scenes ?? []);
    this.providerErrors.set(response.providerErrors ?? []);
    this.truncatedProviders.set(response.truncated ?? []);
    // The new result already carries the server-side stage, so client-only pending/error states are stale.
    this.loadStatesState.set(new Map());
    this.errorKey.set(null);
  }

  private setLoadState(rowId: string, state: ExternalSceneLoadState): void {
    this.loadStatesState.update(current => {
      const next = new Map(current);
      if (state.status === 'idle') {
        next.delete(rowId);
      } else {
        next.set(rowId, state);
      }
      return next;
    });
  }
}

function statusOf(error: unknown): number | null {
  return error instanceof HttpErrorResponse ? error.status : null;
}
